import {
  CloudDownloadOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  HddOutlined,
  WifiOutlined,
} from '@ant-design/icons';

import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';

import { useCpuUsage, useDiskUsage, useMemoryUsage, useNetworkUsage } from '@/features/monitoring';

import { GaugeCard } from '@/widgets/monitoring/GaugeCard';

import styles from './GaugeMetrics.module.css';

const Content = () => {
  const { data: cpuData, isLoading: cpuLoading } = useCpuUsage();
  const { data: memoryData, isLoading: memoryLoading } = useMemoryUsage();
  const { data: networkData, isLoading: networkLoading } = useNetworkUsage();
  const { data: diskData, isLoading: diskLoading } = useDiskUsage();

  return (
    <div className={styles.gaugeRow}>
      <GaugeCard
        title="CPU 사용률"
        value={cpuData?.percentage ?? 0}
        max={100}
        unit="%"
        isLoading={cpuLoading}
        icon={<DashboardOutlined />}
        thresholds={{ warning: 50, danger: 80 }}
        totalCapacity={cpuData?.total}
        capacityUnit="cores"
      />
      <GaugeCard
        title="메모리 사용률"
        value={memoryData?.percentage ?? 0}
        max={100}
        unit="%"
        isLoading={memoryLoading}
        icon={<DatabaseOutlined />}
        thresholds={{ warning: 60, danger: 85 }}
        totalCapacity={memoryData?.total}
        capacityUnit="GB"
      />
      <GaugeCard
        title="네트워크 송신률"
        value={networkData?.transmit.current ?? 0}
        max={1024}
        unit="KB/s"
        isLoading={networkLoading}
        icon={<WifiOutlined />}
        thresholds={{ warning: 50, danger: 70 }}
      />
      <GaugeCard
        title="네트워크 수신률"
        value={networkData?.receive.current ?? 0}
        max={1024}
        unit="KB/s"
        isLoading={networkLoading}
        icon={<CloudDownloadOutlined />}
        thresholds={{ warning: 50, danger: 70 }}
      />
      <GaugeCard
        title="디스크 쓰기"
        value={diskData?.write.current ?? 0}
        max={1024}
        unit="KB/s"
        isLoading={diskLoading}
        icon={<HddOutlined />}
        thresholds={{ warning: 500, danger: 800 }}
      />
      <GaugeCard
        title="디스크 읽기"
        value={diskData?.read.current ?? 0}
        max={1024}
        unit="KB/s"
        isLoading={diskLoading}
        icon={<HddOutlined />}
        thresholds={{ warning: 500, danger: 800 }}
      />
    </div>
  );
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return (
    <div className={styles.gaugeRow}>
      <ErrorFallback title={title} description={description} {...props} />
    </div>
  );
};

export const GaugeMetrics = { Content, Error };
